import { all, call, select, takeLatest } from 'redux-saga/effects';
import {
  actions,
  selectClinicalTrialReportsState,
} from './clinicalTrialReports.slice';
import {
  ClinicalTrialReportsFetchRequestAction,
  ClinicalTrialReportUpdateStatusRequestAction,
  ClinicalTrialReportUrlFetchRequestAction,
} from '@restore/clinicalTrialReports/clinicalTrialReports.types';
import {
  fetchReports,
  fetchReportUrl,
  changeReportStatus,
} from '@api/clinicalTrialReports';
import { dispatch, Resp } from '@restore/helpers';
import { UserRoles } from '@dynamika-shared/types/auth';
import { fetchClinicalTrialUtilsInventory } from '@api/clinicalTrialInventory';
import { runToast, showErrorToast } from '@restore/tools';
import { fetchTeamMemberListAll } from '@api/teamMembers';

function* fetchClinicalTrialWorker({
  payload,
}: ClinicalTrialReportsFetchRequestAction) {
  try {
    let reportsState: ReturnType<typeof selectClinicalTrialReportsState> =
      yield select(selectClinicalTrialReportsState);

    if (
      payload.clinicalTrialId !== reportsState.dictionaries.clinicalTrialId ||
      !reportsState.dictionaries.reportNamesList ||
      !reportsState.dictionaries.readersList
    ) {
      yield fetchDictionariesWorker(payload.clinicalTrialId);
      reportsState = yield select(selectClinicalTrialReportsState);
    }

    const reportsResponse: Resp<typeof fetchReports> = yield call(
      fetchReports,
      payload
    );

    const data = reportsResponse.data.content?.map(report => {
      return {
        ...report,
        reportName:
          reportsState.dictionaries.reportNamesList?.[
            report.reportTemplateCode
          ] ?? '',
        readers: [
          ...new Set(
            report.assigneeIds.map(
              assigneeId =>
                reportsState.dictionaries.readersList?.[assigneeId] ?? ''
            )
          ),
        ],
      };
    });
    yield dispatch(
      actions.fetchClinicalTrialReportsSuccess({
        reports: data,
        totalElements: reportsResponse.data.totalElements,
      })
    );
  } catch (error) {
    yield showErrorToast(error, 'Failed to load reports');
    yield dispatch(actions.fetchClinicalTrialReportsFailure(error));
  }
}

function* fetchDictionariesWorker(clinicalTrialId: string) {
  try {
    const [usersResponse, inventoryResponse]: [
      Resp<typeof fetchTeamMemberListAll>,
      Resp<typeof fetchClinicalTrialUtilsInventory>,
    ] = yield all([
      call(fetchTeamMemberListAll, clinicalTrialId, UserRoles.READER),
      call(fetchClinicalTrialUtilsInventory, clinicalTrialId),
    ]);
    const flatUsersList: Record<string, string> =
      usersResponse?.data.reduce((acc, user) => {
        return {
          ...acc,
          [user.id]: user.fullName,
        };
      }, {}) ?? {};

    const flatReportNamesList: Record<string, string> =
      inventoryResponse?.data?.reportTemplates?.reduce(
        (acc, { code, name }) => {
          return {
            ...acc,
            [code]: name,
          };
        },
        {}
      );
    yield dispatch(
      actions.fetchDictionariesSuccess({
        clinicalTrialId,
        readersList: flatUsersList,
        reportNamesList: flatReportNamesList,
      })
    );
  } catch (e) {
    yield dispatch(actions.fetchDictionariesFailure(e));
  }
}

export function* fetchReportUrlWorker({
  payload,
}: ClinicalTrialReportUrlFetchRequestAction) {
  try {
    const reportUrlResponse: Resp<typeof fetchReportUrl> = yield call(
      fetchReportUrl,
      payload
    );

    if (!reportUrlResponse.data.url) {
      throw new Error('no url');
    }

    yield dispatch(
      actions.fetchReportUrlSuccess({ url: reportUrlResponse.data.url })
    );
  } catch (error) {
    yield dispatch(actions.fetchReportUrlFailure('Cannot find report'));
    yield showErrorToast(error, 'Cannot find report');
  }
}

export function* updateReportStatusWorker(
  action: ClinicalTrialReportUpdateStatusRequestAction
) {
  try {
    const { data }: Resp<typeof changeReportStatus> = yield call(
      changeReportStatus,
      action.payload
    );

    yield dispatch(actions.updateReportStatusSuccess(data));
    const status =
      action.payload.status === 'APPROVED' ? 'approved' : 'rejected';
    yield call(runToast, 'success', `Report has been ${status} successfully`);
  } catch (error) {
    yield dispatch(actions.updateReportStatusFailure(error));
    yield showErrorToast(error, 'Failed to update report status');
  }
}

export function* clinicalTrialReportsSaga() {
  yield takeLatest(
    actions.fetchClinicalTrialReportsRequest,
    fetchClinicalTrialWorker
  );
  yield takeLatest(actions.fetchReportUrlRequest, fetchReportUrlWorker);
  yield takeLatest(actions.updateReportStatusRequest, updateReportStatusWorker);
}
